import React, { useState, useEffect, useMemo } from 'react';
import { RichTextEditor } from 'design-system';
import {
  TextField,
  Spinner,
  SelectMenu,
  Button,
  Typography,
  Tooltip,
  ConfirmDialog,
} from '@scale/scaleui';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getSendgridTemplates,
  createEmailCommsTemplate,
  updateEmailCommsTemplate,
} from 'react-scale/corp/lib/api/growth';
import { useUser } from 'react-scale/components/ui/UserContext';
import {
  SendgridTemplateData,
  EmailCommsTemplateStatus,
  EmailCommsTemplateTeam,
  EmailTemplateData,
  OutlierEmailSubscriptionSegment,
  EmailSubscriptionSegment,
  GROWTH_EMAIL_TEMPLATE_IDS,
  isEmailSegmentRequired,
} from '@scale/shared-types';
import TemplatePreview from 'react-scale/components/GrowthCenter/EmailDripCampaign/CampaignSetup/EmailTemplateModalV2/TemplatePreview';
import SAlert from 'react-s-alert';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { useHistory } from 'react-router-dom';
import { ClickableCopyToClipboardChip } from 'react-scale/components/GrowthCenter/EmailDripCampaign/CampaignSetup/EmailTemplateModalV2/AvailableVariablesList';
import { validateTemplateVariable } from '@scale/shared/utils/templateUtils';

const TemplateStatusOptions = [
  { id: EmailCommsTemplateStatus.Public, name: EmailCommsTemplateStatus.Public },
  { id: EmailCommsTemplateStatus.Internal, name: EmailCommsTemplateStatus.Internal },
];

const TemplateTeamOptions = Object.values(EmailCommsTemplateTeam).map(team => ({
  id: team,
  name: team,
}));

const EmailSegmentOptions = Object.values(OutlierEmailSubscriptionSegment).map(segment => ({
  id: segment,
  name: segment,
}));

// eslint-disable-next-line complexity
const TemplateCreationForm = ({ template }: { template?: EmailTemplateData }) => {
  const user = useUser();
  const history = useHistory();
  const queryClient = useQueryClient();

  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(today.getDate()).padStart(2, '0')}`;
  const uploadImgBase = `upload/communication-center/${formattedDate}/${user.id}`;

  const [sendgridTemplate, setSendgridTemplate] = useState<SendgridTemplateData | undefined>();
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [status, setStatus] = useState(template?.status ?? EmailCommsTemplateStatus.Internal);
  const [team, setTeam] = useState<EmailCommsTemplateTeam>(
    template?.team ?? EmailCommsTemplateTeam.Growth,
  );
  const [emailSegment, setEmailSegment] = useState<EmailSubscriptionSegment | undefined>(
    template?.emailSegment,
  );
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setSubject(template.subject);
      setBody(template.body);
      setStatus(template.status);
      setTeam(template.team ?? EmailCommsTemplateTeam.Growth);
      setEmailSegment(template.emailSegment);
    }
  }, [template]);

  const { isLoading, data: sendgridTemplates } = useQuery(
    'get-sendgrid-templates',
    getSendgridTemplates,
  );

  const { isLoading: createTemplateLoading, mutate: createTemplate } = useMutation(
    async () =>
      await createEmailCommsTemplate({
        userId: user.id,
        body,
        subject,
        sendgridTemplateId: sendgridTemplate!.id,
        status,
        team,
        emailSegment,
        name,
      }),
    {
      onSuccess: () => {
        SAlert.success('Template created!', {
          timeout: 10000,
        });
        queryClient.invalidateQueries('get-email-comms-templates');
        queryClient.invalidateQueries('get-email-comms-template-versions');
        history.push(`${Routes.TemplatesLibrary}`);
      },
    },
  );

  const handleTemplateUpdate = async (status: EmailCommsTemplateStatus, successMessage: string) => {
    try {
      if (!template) {
        return;
      }
      await updateEmailCommsTemplate(template.templateId, {
        userId: user.id,
        body,
        subject,
        sendgridTemplateId: sendgridTemplate!.id,
        status,
        team,
        emailSegment,
        name,
      });

      SAlert.success(successMessage, { timeout: 10000 });
      queryClient.invalidateQueries('get-email-comms-templates');
      queryClient.invalidateQueries('get-email-comms-template-versions');
      history.push(`${Routes.TemplatesLibrary}`);
    } catch (error) {
      SAlert.error('Something went wrong. Please try again.', { timeout: 10000 });
    }
  };

  const { isLoading: updateTemplateLoading, mutate: updateTemplate } = useMutation(() =>
    handleTemplateUpdate(status, 'Template updated!'),
  );

  const { isLoading: archiveTemplateLoading, mutate: archiveTemplate } = useMutation(() =>
    handleTemplateUpdate(EmailCommsTemplateStatus.Deprecated, 'Template archived!'),
  );

  // TODO: Replace with Handlebars
  const variables: string[] = useMemo(() => {
    const variableSet = new Set<string>();

    const variableRegex = /\{\{(.*?)\}\}/g;
    let match;
    while ((match = variableRegex.exec(body)) !== null) {
      variableSet.add(match[1]);
    }
    while ((match = variableRegex.exec(subject)) !== null) {
      variableSet.add(match[1]);
    }

    return [...variableSet];
  }, [body, subject]);

  const hasInvalidVariables = useMemo(
    () => variables.some(variable => !validateTemplateVariable(variable)),
    [variables],
  );

  const requiresEmailSegment = useMemo(() => {
    if (!sendgridTemplate) return false;
    return (
      sendgridTemplate.id === GROWTH_EMAIL_TEMPLATE_IDS.OUTLIER_BANNER ||
      sendgridTemplate.id === GROWTH_EMAIL_TEMPLATE_IDS.LIFECYCLE_COMMS
    );
  }, [sendgridTemplate]);

  const hasMissingRequiredEmailSegment = useMemo(
    () => requiresEmailSegment && !emailSegment,
    [requiresEmailSegment, emailSegment],
  );

  useEffect(() => {
    if (!isLoading && sendgridTemplates) {
      let currentSendgridTemplate = sendgridTemplates[0];
      if (template) {
        currentSendgridTemplate =
          sendgridTemplates.find(
            sendgridTemplate => sendgridTemplate.id === template.sendgridTemplateId,
          ) ?? sendgridTemplates[0];
      }
      setSendgridTemplate(currentSendgridTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, sendgridTemplates, template?.sendgridTemplateId]);

  const handleSendgridTemplateChange = (sendgridTemplateId: string) => {
    const template = sendgridTemplates!.find(
      sendgridTemplate => sendgridTemplate.id === sendgridTemplateId,
    );

    setSendgridTemplate(template);
    // Reset emailSegment if switching away from templates that require it
    if (!isEmailSegmentRequired(sendgridTemplateId)) {
      setEmailSegment(undefined);
    }
    setIsFormChanged(true);
  };

  const createOrUpdateTemplate = async () => {
    if (template) {
      await updateTemplate();
    } else {
      await createTemplate();
    }
  };

  const handleArchiveTemplate = async () => {
    await archiveTemplate();
  };

  if (isLoading || !sendgridTemplates) {
    return (
      <div className="flex h-full w-full justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex-grow flex flex-col gap-4">
      <div className="gap-4 flex w-full">
        <div className="flex flex-col gap-2 w-1/2">
          <TextField
            value={name}
            onChange={e => {
              setName(e.target.value);
              setIsFormChanged(true);
            }}
            label="Template name"
            description={
              template
                ? 'Note: updating the template name will rename previous versions of this template.'
                : ''
            }
          />
          <SelectMenu
            label="Banner Image"
            menuDirection="bottom"
            options={sendgridTemplates}
            selectedOptionId={sendgridTemplate?.id ?? ''}
            setSelected={handleSendgridTemplateChange}
          />
          <SelectMenu
            label="Privacy"
            menuDirection="bottom"
            options={TemplateStatusOptions}
            selectedOptionId={status}
            setSelected={status => {
              setStatus(status as EmailCommsTemplateStatus);
              setIsFormChanged(true);
            }}
          />
          <SelectMenu
            label="Team"
            menuDirection="bottom"
            options={TemplateTeamOptions}
            selectedOptionId={team}
            setSelected={team => {
              setTeam(team as EmailCommsTemplateTeam);
              setIsFormChanged(true);
            }}
          />
          {requiresEmailSegment && (
            <SelectMenu
              label="Email Segment"
              menuDirection="bottom"
              options={EmailSegmentOptions}
              selectedOptionId={emailSegment ?? ''}
              setSelected={segment => {
                setEmailSegment(segment as EmailSubscriptionSegment);
                setIsFormChanged(true);
              }}
              required
            />
          )}
          <TextField
            value={subject}
            onChange={e => {
              setSubject(e.target.value);
              setIsFormChanged(true);
            }}
            label="Subject"
          />

          <div className="relative flex-grow">
            <RichTextEditor
              className="h-[500px]"
              value={body}
              onChange={val => {
                setBody(val);
                setIsFormChanged(true);
              }}
              options={{
                uploadImgBase,
              }}
              enableGrammarChecking
            />
          </div>

          {variables.length === 0 && (
            <Typography.Paragraph3 className="text-neutral-600">
              {`Please use double curly braces for variables and follow the `}
              <a
                className="text-primary-500"
                href="https://en.wikipedia.org/wiki/Snake_case"
                target="_blank"
                rel="noopener noreferrer"
              >
                snake_case
              </a>
              {` convention - {{variable_name}}.`}
            </Typography.Paragraph3>
          )}
          {variables.length > 0 && (
            <div className="flex gap-1 items-center">
              <Typography.Paragraph2 className="text-neutral-600 inline-block">
                Variables:
              </Typography.Paragraph2>
              {variables.map((variable: string) => ClickableCopyToClipboardChip(variable))}
            </div>
          )}
        </div>
        <div className="relative flex-grow">
          <TemplatePreview
            className="absolute"
            templateHTML={sendgridTemplate?.htmlWrapper ?? ''}
            content={body}
            subject={subject}
            sendgridTemplateId={sendgridTemplate?.id}
          />
        </div>
      </div>
      <div className="flex justify-end gap-x-2 pb-4">
        {template && (
          <Button
            size="lg"
            disabled={archiveTemplateLoading}
            variant="neutral"
            onClick={() => setArchiveConfirmationOpen(true)}
          >
            Archive
          </Button>
        )}
        <Tooltip
          side="top"
          target={
            <Button
              size="lg"
              disabled={
                !subject ||
                !name ||
                !body ||
                hasInvalidVariables ||
                hasMissingRequiredEmailSegment ||
                !isFormChanged
              }
              onClick={createOrUpdateTemplate}
              isLoading={createTemplateLoading || updateTemplateLoading}
            >
              {template ? 'Update' : 'Create'}
            </Button>
          }
          text={
            hasInvalidVariables
              ? 'Please make sure your variables follow snake_case (all alphanumeric characters, lowercase, separated by underscores).'
              : 'Make sure all fields are filled out before creating the template.'
          }
        />
        <ConfirmDialog
          open={archiveConfirmationOpen}
          className={'z-50'}
          onConfirm={() => {
            setArchiveConfirmationOpen(false);
            handleArchiveTemplate();
          }}
          onCancel={() => setArchiveConfirmationOpen(false)}
        >
          Are you sure you want to archive this template? You will not be able to use it in future
          campaigns.
        </ConfirmDialog>
      </div>
    </div>
  );
};

export default TemplateCreationForm;
